import React, { useCallback } from "react"
import { Tooltip } from "react-tooltip"

export interface NaicFormGroupsProps {
  naics: string[]
  label: string
  tooltip?: string
  idPrefix: string
  labelColumns?: number
  labelClass?: string
  inputClass?: string
  hr?: boolean
  onChange: (naics: string[]) => void
}

const DEFAULT_TOOLTIP = "The National Association of Insurance Commissioner (NAIC) number that uniquely identifies the registering insurance plan and any affiliates for whom the registrant will be submitting data. This value should be entered as a 5-digit number. The NAIC number may not be applicable to all submitters or all affiliates; if the NAIC number does not apply to your organization, please leave blank."

export function NaicFormGroups({
  naics,
  label,
  tooltip,
  idPrefix,
  labelColumns = 4,
  labelClass = "control-label",
  inputClass = "col-sm-4",
  hr,
  onChange,
}: NaicFormGroupsProps): React.ReactNode {
  if (naics.length === 0) {
    naics = [""] // Ensure there is at least one empty input
  }

  const addHandler = useCallback(() => {
    onChange([...naics, ""])
  }, [naics, onChange])

  const removeHandler = useCallback(() => {
    onChange(naics.filter((_, i) => i !== naics.length - 1))
  }, [naics, onChange])

  // TODO: there must be a way to implement this without two nested callbacks,
  // but it works for now.
  const changeHandler = useCallback((index: number, newValue: string) => {
    onChange(naics.map((ov, i) => i === index ? newValue : ov))
  }, [naics, onChange])

  const labelId = `${idPrefix}_label`

  return (<>
    {naics.map((naic, index, naics) => {
      const isFirst = index === 0
      const isLast = index === naics.length - 1
      return (
        <div className="naic omit-child-label" key={`naic_${index}`}>
          <div className="form-group">
            {isFirst && (
              <label className={`col-sm-${labelColumns} ${labelClass}`} htmlFor={`${idPrefix}_${index}`} id={labelId}>
                {label} &nbsp;
                <i className="fa fa-question-circle" data-tooltip-id="tip-naic" data-tooltip-content={tooltip ?? DEFAULT_TOOLTIP} />
                <Tooltip id="tip-naic" className="registration-tooltip" />
              </label>
            )}
            {!isFirst && <div className={`col-sm-${labelColumns}`} />}
            <div className={inputClass}>
              <Input
                id={`${idPrefix}_${index}`}
                labelId={labelId}
                value={naic ?? ""}
                index={index}
                onChange={changeHandler}
              />
            </div>

            {naics.length > 1 && isLast && (
              <div className="col-sm-4 hidden-print" onClick={removeHandler}>
                <p className="add-another">
                  <span className="fa fa-minus-circle fa-lg text-danger remove-this" />&nbsp;&nbsp;Remove this
                </p>
              </div>
            )}
          </div>
        </div>
      )
    })}

    <div className="form-group hidden-print" onClick={addHandler}>
      <div className={`col-sm-offset-${labelColumns} col-sm-${12 - labelColumns}`}>
        <p className="add-another">
          <i className="fa fa-plus-circle fa-lg text-success add-another" />
                &nbsp;&nbsp;Add Another NAIC Number
        </p>
      </div>
      {hr && (<div className="col-sm-12">
        <hr style={{ margin: "5px 0" }} />
      </div>)}
    </div>
  </>)
}

interface InputProps {
  id: string
  labelId: string
  value: string
  index: number
  onChange: (index: number, value: string) => void
}
function Input({
  id,
  labelId,
  value,
  index,
  onChange,
}: InputProps) {
  const changeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, e.currentTarget.value)
  }, [index, onChange])

  return (
    <input
      id={id}
      aria-labelledby={labelId}
      value={value}
      onChange={changeHandler}
      className="form-control"
      placeholder="NAIC Number (5 digits)"
      data-fv-naic-message="The value is not a valid NAIC"
      maxLength={5}
    />
  )
}
